import type { Workspace, WorkspaceRole } from '../../types/workspace'
import type { TimelineBoardKey } from '../../types/schema'
import { isPersonalWorkspace } from '../../utils/workspaceKind'
import { isModuleEnabled, moduleForLayoutView, type ModuleKey, type WorkspaceModuleSource } from '../../utils/workspaceModules'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { workspaceRootPath, toConsolePath } from '../../utils/consoleRoutes'
import { buildMemberPath, type MemberTabKey, type MemberAiSubTab } from '../../member/memberAppRouting'
import { getWrapperRole } from '../../utils/wrapperRole'

export type SwitchLandingApp = 'WEB' | 'MEMBER' | 'HELPER'

export interface SwitchLandingCurrentPage {
  // Web:
  activeLayoutView?: string
  subView?: string
  board?: string

  // Member:
  tab?: string
  subTab?: string

  // Helper:
  helperTab?: string
  controlsSubTab?: string
  fieldSubTab?: string

  // Raw / generic:
  pathname?: string
  search?: string
}

export type SwitchLandingCurrent = SwitchLandingCurrentPage | string | null | undefined

export type SwitchLandingTarget = Pick<Workspace, 'id' | 'slug' | 'isPersonal' | 'kind'> & {
  modules?: Workspace['modules']
  isPreview?: boolean
  previewSurfaceMask?: Workspace['previewSurfaceMask']
}

export interface ResolveSwitchLandingOptions {
  app: SwitchLandingApp
  current?: SwitchLandingCurrent
  target: SwitchLandingTarget
  role?: WorkspaceRole | string | null
  isAllowed?: (pageKey: string) => boolean
  helperPolicy?: unknown
}

const HELPER_PATH_BY_TAB: Record<string, string> = {
  TASKS: '/helper/dashboard',
  CALENDAR: '/helper/calendar',
  MEETINGS: '/helper/meetings',
  CHAT: '/helper/chat',
  FIELD: '/helper/field',
  SCHOOL: '/helper/school',
}

const BOARD_CODES = new Set(['d', 'dash', 'v', 'visionboard', 'o', 'operations', 'r', 'roadmap'])

/**
 * Named hook for helper app tab policy (S15 will wire policy enforcement).
 * Until S15 lands, helper tabs are allowed by default under policy.
 */
export const isHelperTabPolicyAllowed = (
  tab: string,
  target: ResolveSwitchLandingOptions['target'],
  policy?: unknown,
): boolean => {
  void tab
  void target
  void policy
  return true
}

/**
 * Parses current view/tab into a canonical page key for the given app.
 */
export const extractPageKey = (
  app: SwitchLandingApp,
  current?: SwitchLandingCurrent,
): { pageKey: string; subTab?: string } => {
  if (!current) {
    return { pageKey: app === 'WEB' ? 'DASHBOARD' : app === 'MEMBER' ? 'for-you' : 'TASKS' }
  }

  if (typeof current === 'string') {
    // Check if it's a pathname
    if (current.startsWith('/')) {
      const cleanPath = current.split(/[?#]/)[0]
      const segments = cleanPath.split('/').filter(Boolean)
      const offset = segments[0] === 'console' ? 1 : 0
      const root = segments[offset]

      if (app === 'WEB') {
        if (root === 'workspace') {
          const next = segments[offset + 2]
          if (!next) return { pageKey: 'DASHBOARD' }

          if (BOARD_CODES.has(next.toLowerCase())) {
            const viewSegment = segments[offset + 3]
            const subViewSegment = segments[offset + 4]
            if (!viewSegment) return { pageKey: 'DASHBOARD' }

            const upperView = viewSegment.toUpperCase()
            if (upperView === 'AI') return { pageKey: 'AI_MAGIC', subTab: subViewSegment }
            if (upperView === 'SETTINGS') return { pageKey: 'WORKSPACE_SETTINGS', subTab: subViewSegment }
            return { pageKey: upperView, subTab: subViewSegment }
          }

          const upperNext = next.toUpperCase()
          if (upperNext === 'SETTINGS') {
            return { pageKey: 'WORKSPACE_SETTINGS', subTab: segments[offset + 3] }
          }
          return { pageKey: upperNext, subTab: segments[offset + 3] }
        }

        if (root === 'personal') {
          const next = segments[offset + 1]
          if (!next) return { pageKey: 'DASHBOARD' }

          if (BOARD_CODES.has(next.toLowerCase())) {
            const viewSegment = segments[offset + 2]
            const subViewSegment = segments[offset + 3]
            if (!viewSegment) return { pageKey: 'DASHBOARD' }

            const upperView = viewSegment.toUpperCase()
            if (upperView === 'AI') return { pageKey: 'AI_MAGIC', subTab: subViewSegment }
            if (upperView === 'SETTINGS') return { pageKey: 'WORKSPACE_SETTINGS', subTab: subViewSegment }
            return { pageKey: upperView, subTab: subViewSegment }
          }

          const upperNext = next.toUpperCase()
          if (upperNext === 'SETTINGS') {
            return { pageKey: 'WORKSPACE_SETTINGS', subTab: segments[offset + 2] }
          }
          return { pageKey: upperNext, subTab: segments[offset + 2] }
        }

        if (root) return { pageKey: root.toUpperCase() }
        return { pageKey: 'DASHBOARD' }
      }

      if (app === 'MEMBER') {
        // e.g. /member/slug/board
        const tab = segments[offset + 2] || segments[offset + 1] || 'for-you'
        const sub = segments[offset + 3]
        return { pageKey: tab, subTab: sub }
      }

      if (app === 'HELPER') {
        // e.g. /helper/calendar, /helper/field?subtab=count
        const tab = (segments[offset + 1] || 'dashboard').toUpperCase()
        const subQuery = current.includes('?') ? new URLSearchParams(current.split('?')[1]).get('subtab') : undefined
        if (tab === 'DASHBOARD' || tab === 'TASKS') return { pageKey: 'TASKS' }
        if (tab === 'FIELD' || tab === 'CONTROLS' || tab === 'SCAN') {
          return { pageKey: 'FIELD', subTab: subQuery?.toUpperCase() }
        }
        return { pageKey: tab, subTab: subQuery?.toUpperCase() }
      }
    }

    return { pageKey: current }
  }

  // Object shape:
  if (app === 'WEB') {
    const key = current.activeLayoutView || current.subView || 'DASHBOARD'
    return { pageKey: key, subTab: current.subView }
  }

  if (app === 'MEMBER') {
    const tab = current.tab || 'for-you'
    return { pageKey: tab, subTab: current.subTab }
  }

  if (app === 'HELPER') {
    const tab = (current.helperTab || current.tab || 'TASKS').toUpperCase()
    const sub = (current.fieldSubTab || current.controlsSubTab || current.subTab)?.toUpperCase()
    return { pageKey: tab, subTab: sub }
  }

  return { pageKey: 'HOME' }
}

/**
 * Single evaluator combining role, modules, workspace kind (ADR-0001), and helper policy.
 */
export const isLandingAllowed = ({
  app,
  pageKey,
  subTab,
  target,
  role,
  helperPolicy,
}: {
  app: SwitchLandingApp
  pageKey: string
  subTab?: string
  target: SwitchLandingTarget
  role?: WorkspaceRole | string | null
  helperPolicy?: unknown
}): boolean => {
  const isPersonal = isPersonalWorkspace(target)
  const canonicalRole = (role || (isPersonal ? 'OWNER' : 'VIEWER')) as WorkspaceRole

  if (app === 'WEB') {
    const upperKey = pageKey.toUpperCase()

    // Helpers are restricted from desktop web console
    if (canonicalRole === 'HELPER') {
      return false
    }

    // Personal workspace restrictions (ADR-0001 §D3)
    if (isPersonal) {
      const personalBlockedViews = new Set([
        'CHAT',
        'MEETINGS',
        'UPLOAD',
        'LINK',
        'INVITES',
        'MEMBERS',
        'TEAM',
        'STOCK',
        'BUDGET',
        'QUOTA',
        'RAID',
        'CONSTRUCTION',
        'HOSPITAL',
        'SITES',
        'TIMESHEETS',
        'DELIVERY',
      ])
      if (personalBlockedViews.has(upperKey)) {
        return false
      }
    }

    // Role restrictions
    if (upperKey === 'WORKSPACE_SETTINGS' && subTab === 'admin') {
      if (!roleCanPerform(canonicalRole, 'ADMIN')) return false
    }
    if (upperKey === 'MANAGE' && subTab === 'board') {
      if (!roleCanPerform(canonicalRole, 'EDITOR')) return false
    }

    // Module restrictions (isModuleEnabled)
    const mod = moduleForLayoutView(upperKey)
    if (mod && !isModuleEnabled(target as WorkspaceModuleSource, mod as ModuleKey)) {
      return false
    }

    return true
  }

  if (app === 'MEMBER') {
    const lowerKey = pageKey.toLowerCase()

    // Personal workspace restrictions (ADR-0001 §D3)
    if (isPersonal) {
      if (lowerKey === 'team' || lowerKey === 'stock') {
        return false
      }
      if (lowerKey === 'nexus' && subTab?.toUpperCase() === 'MEETINGS') {
        return false
      }
    }

    // Module restrictions
    if (lowerKey === 'forms' && !isModuleEnabled(target as WorkspaceModuleSource, 'forms')) {
      return false
    }
    if (lowerKey === 'stock' && !isModuleEnabled(target as WorkspaceModuleSource, 'stock')) {
      return false
    }
    if (lowerKey === 'automations' && !isModuleEnabled(target as WorkspaceModuleSource, 'automate')) {
      return false
    }
    if (lowerKey === 'nexus' && subTab?.toUpperCase() === 'MEETINGS' && !isModuleEnabled(target as WorkspaceModuleSource, 'meetings')) {
      return false
    }

    return true
  }

  if (app === 'HELPER') {
    const upperKey = pageKey.toUpperCase()

    // Helpers only operate on team workspaces
    if (isPersonal) {
      return false
    }

    // Module restrictions
    if (upperKey === 'MEETINGS' && !isModuleEnabled(target as WorkspaceModuleSource, 'meetings')) {
      return false
    }
    if (upperKey === 'FIELD') {
      const upperSub = subTab?.toUpperCase()
      if (upperSub === 'SCAN' && target.modules?.helperScanEnabled === false) {
        return false
      }
      if (upperSub === 'COUNT') {
        if (!isModuleEnabled(target as WorkspaceModuleSource, 'stock') || target.modules?.helperCountEnabled === false) {
          return false
        }
      }
    }

    // Helper policy hook
    return isHelperTabPolicyAllowed(upperKey, target, helperPolicy)
  }

  return true
}

/**
 * Universal Switch Landing Resolver (WS6 / Plan S4 Step 1).
 * Pure function: role/module/kind come in as data for full unit-testability.
 * Allowed → same page with item-level params dropped.
 * Else → workspace home (web dashboard, member For You, helper Tasks).
 */
export const resolveSwitchLanding = ({
  app,
  current,
  target,
  role,
  isAllowed,
  helperPolicy,
}: ResolveSwitchLandingOptions): string => {
  const { pageKey, subTab } = extractPageKey(app, current)
  const isPersonal = isPersonalWorkspace(target)
  const effectiveRole = (role || (isPersonal ? 'OWNER' : 'VIEWER')) as WorkspaceRole

  const allowed = isAllowed
    ? isAllowed(pageKey)
    : isLandingAllowed({
        app,
        pageKey,
        subTab,
        target,
        role: effectiveRole,
        helperPolicy,
      })

  if (app === 'WEB') {
    const home = workspaceRootPath(target, 'DASHBOARD')
    if (!allowed) return home

    const upperKey = pageKey.toUpperCase()
    if (upperKey === 'DASHBOARD') return home

    if (upperKey === 'CHAT') {
      return isPersonal ? home : toConsolePath(`/workspace/${target.slug}/chat`)
    }
    if (upperKey === 'FORMS') {
      return isPersonal ? toConsolePath('/personal/forms') : toConsolePath(`/workspace/${target.slug}/forms`)
    }
    if (upperKey === 'MEETINGS') {
      return isPersonal ? home : toConsolePath(`/workspace/${target.slug}/meetings`)
    }
    if (upperKey === 'AUTOMATE') {
      return isPersonal ? toConsolePath('/personal/automate') : toConsolePath(`/workspace/${target.slug}/automate`)
    }
    if (upperKey === 'WORKSPACE_SETTINGS' || upperKey === 'SETTINGS') {
      if (isPersonal) return toConsolePath('/personal/settings')
      const isAdmin = roleCanPerform(effectiveRole, 'ADMIN')
      return toConsolePath(`/workspace/${target.slug}/dash/settings/${isAdmin ? 'admin' : 'member'}`)
    }
    if (upperKey === 'AI_MAGIC') {
      return isPersonal ? toConsolePath('/personal/dash/ai') : toConsolePath(`/workspace/${target.slug}/dash/ai`)
    }
    if (upperKey === 'CALENDAR') {
      return isPersonal ? toConsolePath('/personal/dash/calendar') : toConsolePath(`/workspace/${target.slug}/dash/calendar`)
    }
    if (upperKey === 'MANAGE') {
      return isPersonal ? toConsolePath('/personal/dash/manage') : toConsolePath(`/workspace/${target.slug}/dash/manage`)
    }
    if (upperKey === 'CHECKLISTS') {
      return isPersonal ? toConsolePath('/personal/dash/checklists') : toConsolePath(`/workspace/${target.slug}/dash/checklists`)
    }
    if (upperKey === 'SUMMARY') {
      return isPersonal ? toConsolePath('/personal/dash/summary') : toConsolePath(`/workspace/${target.slug}/dash/summary`)
    }
    if (upperKey === 'REPORTS') {
      return isPersonal ? toConsolePath('/personal/dash/reports') : toConsolePath(`/workspace/${target.slug}/dash/reports`)
    }
    if (upperKey === 'UPLOAD') {
      return isPersonal ? home : toConsolePath(`/workspace/${target.slug}/upload`)
    }
    if (upperKey === 'LINK' || upperKey === 'LINK-HUB') {
      return isPersonal ? home : toConsolePath(`/workspace/${target.slug}/link`)
    }
    if (upperKey === 'INVITES' || upperKey === 'MEMBERS' || upperKey === 'TEAM') {
      return isPersonal ? home : toConsolePath(`/workspace/${target.slug}/team`)
    }
    if (upperKey === 'USER_SETTINGS') return toConsolePath('/settings')
    if (upperKey === 'YOUR_PAGE') return toConsolePath('/your')
    if (upperKey === 'FOCUS') return toConsolePath('/focus')
    if (upperKey === 'PURCHASE') return toConsolePath('/purchase')
    if (upperKey === 'EXPLORE') return toConsolePath('/explore')

    return home
  }

  if (app === 'MEMBER') {
    const home = buildMemberPath({ workspace: target, tab: 'for-you' })
    if (!allowed) return home

    const lowerKey = pageKey.toLowerCase() as MemberTabKey
    if (lowerKey === 'for-you') return home
    if (lowerKey === 'board') return buildMemberPath({ workspace: target, tab: 'board' })
    if (lowerKey === 'nexus') {
      if (subTab?.toUpperCase() === 'MEETINGS' && (isPersonal || !isModuleEnabled(target as WorkspaceModuleSource, 'meetings'))) {
        return home
      }
      return buildMemberPath({ workspace: target, tab: 'nexus' })
    }
    if (lowerKey === 'controls') return buildMemberPath({ workspace: target, tab: 'automations' })
    if (lowerKey === 'forms') return buildMemberPath({ workspace: target, tab: 'forms' })
    if (lowerKey === 'ai') return buildMemberPath({ workspace: target, tab: 'ai', subTab: subTab as MemberAiSubTab })
    if (lowerKey === 'sync') return buildMemberPath({ workspace: target, tab: 'sync' })
    if (lowerKey === 'automations') return buildMemberPath({ workspace: target, tab: 'automations' })
    if (lowerKey === 'team') return isPersonal ? home : buildMemberPath({ workspace: target, tab: 'team' })
    if (lowerKey === 'stock') return isPersonal ? home : buildMemberPath({ workspace: target, tab: 'stock' })
    if (lowerKey === 'you') return buildMemberPath({ workspace: target, tab: 'you' })

    return home
  }

  if (app === 'HELPER') {
    const home = HELPER_PATH_BY_TAB.TASKS
    if (!allowed) return home

    const upperKey = pageKey.toUpperCase()
    if (upperKey === 'TASKS') return HELPER_PATH_BY_TAB.TASKS
    if (upperKey === 'CALENDAR') return HELPER_PATH_BY_TAB.CALENDAR
    if (upperKey === 'MEETINGS') return HELPER_PATH_BY_TAB.MEETINGS
    if (upperKey === 'CHAT' || upperKey === 'NEXUS') return HELPER_PATH_BY_TAB.CHAT
    if (upperKey === 'SCHOOL') return HELPER_PATH_BY_TAB.SCHOOL
    if (upperKey === 'FIELD' || upperKey === 'CONTROLS' || upperKey === 'SCAN') {
      const upperSub = subTab?.toUpperCase()
      if (upperSub && upperSub !== 'TODAY') {
        return `/helper/field?subtab=${upperSub.toLowerCase()}`
      }
      return HELPER_PATH_BY_TAB.FIELD
    }

    return home
  }

  return '/'
}

/**
 * Returns a workspace dashboard landing path only when the path is complete
 * (personal workspace, or team workspace with a non-empty slug), else undefined.
 */
export const safeWorkspaceLanding = (
  workspace?: (Pick<Workspace, 'isPersonal' | 'kind'> & { slug?: string | null }) | null,
  board: TimelineBoardKey = 'DASHBOARD',
): string | undefined => {
  if (!workspace) return undefined
  if (isPersonalWorkspace(workspace)) {
    return workspaceRootPath(workspace as Parameters<typeof workspaceRootPath>[0], board)
  }
  if (typeof workspace.slug === 'string' && workspace.slug.trim().length > 0) {
    return workspaceRootPath({ ...workspace, slug: workspace.slug.trim() }, board)
  }
  return undefined
}

/**
 * Returns a member path only when the workspace target is complete
 * (personal workspace, or team workspace with a non-empty slug), else undefined.
 */
export const safeMemberLanding = (
  args: {
    workspace?: (Pick<Workspace, 'isPersonal' | 'kind'> & { slug?: string | null; id?: string }) | string | null
    tab?: MemberTabKey | string
    subTab?: string
    query?: Record<string, string | number | boolean | null | undefined> | string
  },
): string | undefined => {
  const { workspace } = args
  if (!workspace) return undefined
  if (typeof workspace === 'string') {
    const trimmed = workspace.trim()
    if (!trimmed) return undefined
    return buildMemberPath({ ...args, workspace: trimmed })
  }
  if (isPersonalWorkspace(workspace)) {
    return buildMemberPath(args as Parameters<typeof buildMemberPath>[0])
  }
  if (typeof workspace.slug === 'string' && workspace.slug.trim().length > 0) {
    return buildMemberPath(args as Parameters<typeof buildMemberPath>[0])
  }
  return undefined
}

/**
 * Resolves the AI tab landing path for a given target workspace.
 * Pick shell from getWrapperRole() when not explicitly provided:
 * - member -> safeMemberLanding({ workspace, tab: 'ai' })
 * - web -> web AI path only when target is personal or has a non-empty slug, else undefined
 * - helper -> undefined
 * No id-as-slug fallback.
 */
export const resolveAiLanding = (
  target?: (Pick<Workspace, 'isPersonal' | 'kind'> & { id?: string; slug?: string | null }) | null,
  shell?: 'member' | 'web' | 'helper',
): string | undefined => {
  const effectiveShell = shell ?? (getWrapperRole() === 'member' ? 'member' : getWrapperRole() === 'helper' ? 'helper' : 'web')
  if (effectiveShell === 'helper') {
    return undefined
  }
  if (effectiveShell === 'member') {
    return safeMemberLanding({ workspace: target, tab: 'ai' })
  }
  if (!target) return undefined
  if (isPersonalWorkspace(target)) {
    return toConsolePath('/personal/dash/ai')
  }
  if (typeof target.slug === 'string' && target.slug.trim().length > 0) {
    return toConsolePath(`/workspace/${target.slug.trim()}/dash/ai`)
  }
  return undefined
}

export interface BuildUrlRestoreOnStayOptions {
  wasBack: boolean
  settledPath?: string | null
}

export const buildUrlRestoreOnStay = (options: BuildUrlRestoreOnStayOptions): (() => void) => {
  const { wasBack, settledPath } = options
  return () => {
    if (typeof window === 'undefined') return
    if (wasBack) {
      window.history.forward()
      return
    }
    if (!settledPath) return
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (settledPath === currentUrl) return

    window.history.replaceState(window.history.state, '', settledPath)
    if (typeof PopStateEvent !== 'undefined') {
      window.dispatchEvent(new PopStateEvent('popstate'))
    } else {
      window.dispatchEvent(new Event('popstate'))
    }
  }
}

